import type { Job } from "../../providers/fakerest/dataGenerator/types";

export interface JobStage {
  value: Job["status"];
  label: string;
  color: string;
}

export const JOB_STAGES: JobStage[] = [
  {
    value: "new_lead",
    label: "New Lead",
    color: "bg-blue-500 dark:bg-blue-600",
  },
  {
    value: "inspection_scheduled",
    label: "Inspection Scheduled",
    color: "bg-yellow-500 dark:bg-yellow-600",
  },
  {
    value: "proposal_sent",
    label: "Proposal Sent",
    color: "bg-purple-500 dark:bg-purple-600",
  },
  {
    value: "negotiation",
    label: "Negotiation",
    color: "bg-orange-500 dark:bg-orange-600",
  },
  { value: "won", label: "Won", color: "bg-green-500 dark:bg-green-600" },
  {
    value: "in_progress",
    label: "In Progress",
    color: "bg-cyan-500 dark:bg-cyan-600",
  },
  {
    value: "completed",
    label: "Completed",
    color: "bg-emerald-500 dark:bg-emerald-600",
  },
  { value: "lost", label: "Lost", color: "bg-red-500 dark:bg-red-600" },
];

export type JobsByStatus = Record<Job["status"], Job[]>;

export interface SortConfig {
  field: string;
  order: "ASC" | "DESC";
}

export const getJobsByStatus = (
  jobs: Job[],
  sort?: SortConfig,
): JobsByStatus => {
  // Initialize empty arrays for all statuses
  const jobsByStatus: JobsByStatus = JOB_STAGES.reduce((acc, stage) => {
    acc[stage.value] = [];
    return acc;
  }, {} as JobsByStatus);

  // Group jobs by status
  jobs.forEach((job) => {
    if (jobsByStatus[job.status]) {
      jobsByStatus[job.status].push(job);
    } else {
      // If status doesn't exist in our stages, put in first column
      jobsByStatus[JOB_STAGES[0].value].push(job);
    }
  });

  // Sort each column
  JOB_STAGES.forEach((stage) => {
    if (sort && sort.field) {
      // Apply custom sort from filter sidebar
      jobsByStatus[stage.value].sort((a, b) => {
        const aVal = a[sort.field as keyof Job];
        const bVal = b[sort.field as keyof Job];

        // Handle null/undefined values
        if (aVal == null && bVal == null) return 0;
        if (aVal == null) return sort.order === "ASC" ? 1 : -1;
        if (bVal == null) return sort.order === "ASC" ? -1 : 1;

        // Compare values
        let comparison = 0;
        if (typeof aVal === "string" && typeof bVal === "string") {
          comparison = aVal.localeCompare(bVal);
        } else if (typeof aVal === "number" && typeof bVal === "number") {
          comparison = aVal - bVal;
        } else if (aVal instanceof Date && bVal instanceof Date) {
          comparison = aVal.getTime() - bVal.getTime();
        } else {
          // For dates stored as strings
          comparison = String(aVal).localeCompare(String(bVal));
        }

        return sort.order === "ASC" ? comparison : -comparison;
      });
    } else {
      // Default sort by index (for drag-drop positioning)
      jobsByStatus[stage.value].sort((a, b) => a.index - b.index);
    }
  });

  return jobsByStatus;
};

export const findStageLabel = (status: Job["status"]): string => {
  const stage = JOB_STAGES.find((s) => s.value === status);
  return stage?.label ?? status;
};

export const findStageColor = (status: Job["status"]): string => {
  const stage = JOB_STAGES.find((s) => s.value === status);
  return stage?.color ?? "bg-gray-500";
};
